import { motion } from 'framer-motion';
import { Progress } from '@/components/ui/progress';
import { lc } from '@/i18n/copy';

const copy = {
  en: { saved: 'moments saved', lastSaved: 'Last saved', building: "You're building something meaningful.", today: 'today', yesterday: 'yesterday', daysAgo: 'days ago' },
  es: { saved: 'momentos guardados', lastSaved: 'Último guardado', building: 'Estás construyendo algo significativo.', today: 'hoy', yesterday: 'ayer', daysAgo: 'días' },
  fr: { saved: 'moments sauvegardés', lastSaved: 'Dernier sauvegardé', building: 'Vous construisez quelque chose de significatif.', today: "aujourd'hui", yesterday: 'hier', daysAgo: 'jours' },
  pt: { saved: 'momentos salvos', lastSaved: 'Último salvo', building: 'Você está construindo algo significativo.', today: 'hoje', yesterday: 'ontem', daysAgo: 'dias' },
  ar: { saved: 'لحظات محفوظة', lastSaved: 'آخر حفظ', building: 'أنت تبني شيئًا ذا معنى.', today: 'اليوم', yesterday: 'أمس', daysAgo: 'أيام' },
  ja: { saved: '保存された瞬間', lastSaved: '最後の保存', building: 'あなたは意味あるものを築いています。', today: '今日', yesterday: '昨日', daysAgo: '日前' },
  ko: { saved: '저장된 순간', lastSaved: '마지막 저장', building: '의미 있는 것을 만들고 있습니다.', today: '오늘', yesterday: '어제', daysAgo: '일 전' },
  id: { saved: 'momen tersimpan', lastSaved: 'Terakhir disimpan', building: 'Anda sedang membangun sesuatu yang bermakna.', today: 'hari ini', yesterday: 'kemarin', daysAgo: 'hari lalu' },
  hi: { saved: 'संजोए गए पल', lastSaved: 'आखिरी बार सहेजा', building: 'आप कुछ सार्थक बना रहे हैं।', today: 'आज', yesterday: 'कल', daysAgo: 'दिन पहले' },
};

function timeAgo(dateStr: string, c: any): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return c.today;
  if (days === 1) return c.yesterday;
  return `${days} ${c.daysAgo}`;
}

import type { Language } from '@/i18n/types';

interface Props {
  totalSaved: number;
  lastSavedDate: string | null;
  language: Language;
}

export function SavedVaultStats({ totalSaved, lastSavedDate, language }: Props) {
  const c = lc(copy, language);
  const progressValue = Math.min((totalSaved / 20) * 100, 100);

  return (
    <motion.div
      className="px-5 pt-4 pb-2"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-white/80 text-sm font-semibold">
          {totalSaved} {c.saved}
        </span>
        {lastSavedDate && (
          <span className="text-[11px] text-white/35">
            {c.lastSaved}: {timeAgo(lastSavedDate, c)}
          </span>
        )}
      </div>
      <div className="relative">
        <Progress value={progressValue} className="h-1.5 bg-white/5 [&>div]:bg-gradient-to-r [&>div]:from-purple-500 [&>div]:to-rose-400" />
        <div
          className="absolute top-0 h-1.5 rounded-full bg-white/20 blur-sm"
          style={{ width: `${progressValue}%` }}
        />
      </div>
      <p className="text-[11px] text-white/25 mt-1.5 italic">{c.building}</p>
    </motion.div>
  );
}
